import type { PortfolioData } from '../interfaces/IPortfolioData.js';
import { handle } from './errors.js';
import type { AuthedFetch } from './transport.js';

/** The schedule the user has selected in the TopBar, and the site it belongs to.
 *  Passing it makes that project's card derive from the selected schedule rather
 *  than the site's newest — so switching the picker moves the card. */
export interface IPortfolioActiveSchedule {
  siteId: string;
  scheduleNumber: string;
}

/**
 * The portfolio API over an injected transport — each app binds its own
 * `authedFetch` in its `apis/portfolio.ts`. The web passes `verify`/`active`
 * (its Refresh button and schedule picker); mobile calls it bare and reads only
 * `projects`.
 */
export function createPortfolioApi(authedFetch: AuthedFetch) {
  async function fetchPortfolio(
    signal?: AbortSignal,
    verify = false,
    active?: IPortfolioActiveSchedule,
  ): Promise<PortfolioData> {
    // `verify=1` is the deliberate Refresh: it makes the backend re-validate the
    // schedule's cached parse against the source version (re-fetching only if it
    // changed). A plain load omits it and trusts the cache (fast).
    const params = new URLSearchParams();
    if (verify) params.set('verify', '1');
    if (active) {
      params.set('siteId', active.siteId);
      params.set('scheduleNumber', active.scheduleNumber);
    }
    const qs = params.toString();
    const res = await authedFetch(`/api/portfolio${qs ? `?${qs}` : ''}`, { signal });
    if (res.ok) return (await res.json()) as PortfolioData;
    return handle(res);
  }

  return { fetchPortfolio };
}
